// ============================================================================
// TAREAS DEL EQUIPO — módulo cross `tasks` de lealify-base (/api/tasks).
// Port del panel de letsmove (TasksPanel + inlines, 1-jul) con FOTO-EVIDENCIA
// (idea del POS Snowtea, 16-jul): la plantilla/tarea puede exigir foto para
// completarse; el candado real vive en el backend (done sin foto = 400).
// Manager: misma página que la agenda (paridad de la matriz 16-jul), tokens y
// cliente API propios. Sin modales: detalle/historial/recurrentes EN LÍNEA.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import NumInput from '../../../components/ui/NumInput';
import { toast } from 'react-hot-toast';
import { baseApi } from '../../../api/baseClient';
import { useAuth } from '../../../context/AuthContext';
import { hasModule } from '../../../utils/modulePermissions';
import ConfirmDialog, { usePromptDialog } from '../../../components/common/ConfirmDialog';
import {
  ListTodo, Plus, Check, X, Flag, Repeat, UserRound, CircleSlash, History,
  Camera, Image as ImageIcon, Trash2, Save, Pause, Play, Clock,
} from 'lucide-react';

// ── API (vía baseApi: cookie httpOnly + base URL configurable) ──────────────
export interface Task {
  task_id: number; template_id: number | null; title: string; description: string | null;
  status: 'open' | 'done' | 'skipped'; priority: 'normal' | 'high';
  complete_by: string | null; assigned_user_id: number | null; assignee: string | null;
  days_until: number | null; skip_reason: string | null; created_at: string | null;
  done_at: string | null; done_by: number | null; done_by_name: string | null;
  requires_photo: 0 | 1; photo_path: string | null;
  shift?: string | null; // turno de caja (candado de corte, se define en la agenda); aquí solo se MUESTRA
}
interface Summary { open: number; overdue: number; today: number; }
type Recur = 'daily' | 'weekly' | 'monthly';
interface Template {
  template_id: number; title: string; description: string | null; priority: 'normal' | 'high';
  recur: Recur; weekdays: string | null; month_day: number | null; requires_photo: 0 | 1;
  assigned_user_id: number | null; assignee: string | null; active: 0 | 1;
}
interface Assignee { id: number; name: string; role: string; }

function req<T>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const url = `/tasks${path}`;
  if (method === 'GET') return baseApi.get<T>(url);
  if (method === 'POST') return baseApi.post<T>(url, body);
  if (method === 'PATCH') return baseApi.patch<T>(url, body);
  return baseApi.del<T>(url);
}
// photo_path viene como '/api/tasks/N/photo?v=…'; en el manager la base puede ser otro origen.
const photoSrc = (p: string) => baseApi.baseUrl + p.replace(/^\/api/, '');

// Comprime a dataURL webp (máx 1024px para evidencia; el backend acepta hasta 1.5 MB).
function fileToDataUrl(file: File, max = 1024, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Imagen inválida'));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, max / Math.max(width, height));
        width = Math.round(width * scale); height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/webp', quality));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const recurLabel = (t: Pick<Template, 'recur' | 'weekdays' | 'month_day'>) =>
  t.recur === 'daily' ? 'Todos los días'
    : t.recur === 'weekly'
      ? (() => { const d = (t.weekdays || '').split(',').map(Number).filter((n) => n >= 0 && n <= 6); return d.length ? `Cada ${d.map((x) => DIAS[x]).join(', ')}` : 'Semanal'; })()
      : `Día ${t.month_day} de cada mes`;
const firstName = (full: string) => full.trim().split(/\s+/)[0] || full;
const fmtDT = (s: string | null) => {
  if (!s) return ''; const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};
function dueChip(t: Task): { label: string; cls: string } | null {
  if (t.days_until == null) return null;
  if (t.days_until < 0) return { label: t.days_until === -1 ? 'venció ayer' : `venció hace ${-t.days_until}d`, cls: 'bg-rose-500/15 text-rose-600' };
  if (t.days_until === 0) return { label: 'vence hoy', cls: 'bg-amber-500/15 text-amber-600' };
  if (t.days_until === 1) return { label: 'mañana', cls: 'bg-accent/10 text-accent' };
  return { label: `en ${t.days_until}d`, cls: 'bg-surface-2 text-content-2' };
}

const inputCls = 'w-full bg-surface-2 text-content rounded-lg px-3 py-2 text-sm border border-line focus:outline-none focus:ring-2 focus:ring-accent/40';
const chipBtn = (on: boolean, onCls: string) =>
  `flex h-8 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition ${on ? onCls : 'border-line text-content-2 hover:text-content'}`;

// ── Botón/flujo de foto-evidencia (cámara + galería + quitar) ────────────────
function PhotoEvidence({ task, onChanged }: { task: Task; onChanged: () => void }) {
  const camRef = useRef<HTMLInputElement>(null);
  const galRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const open = task.status === 'open';

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      await req('POST', `/${task.task_id}/photo`, { image: dataUrl });
      toast.success('Evidencia guardada');
      onChanged();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo subir la foto'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await req('DELETE', `/${task.task_id}/photo`); onChanged(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo quitar'); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={pick} />
      {task.photo_path ? (
        <>
          {/* La cookie de sesión autentica el <img>; scoping por tenant en el backend */}
          <a href={photoSrc(task.photo_path)} target="_blank" rel="noreferrer" title="Ver evidencia completa">
            <img src={photoSrc(task.photo_path)} alt="Evidencia" className="h-16 w-16 rounded-lg border border-line object-cover" />
          </a>
          {open && (
            <>
              <button type="button" onClick={() => camRef.current?.click()} disabled={busy} className={chipBtn(false, '')}>
                <Camera size={13} /> Re-tomar
              </button>
              <button type="button" onClick={remove} className="flex h-8 items-center gap-1 rounded-lg border border-line px-2 text-xs text-content-2 transition hover:border-rose-400 hover:text-rose-500">
                <Trash2 size={13} /> Quitar
              </button>
            </>
          )}
        </>
      ) : open ? (
        <>
          <button type="button" onClick={() => camRef.current?.click()} disabled={busy}
            className={`flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs font-semibold transition ${task.requires_photo ? 'bg-accent-solid text-white hover:opacity-90' : 'border border-line text-content-2 hover:text-content'}`}>
            <Camera size={13} /> {busy ? 'Subiendo…' : 'Tomar foto'}
          </button>
          <button type="button" onClick={() => galRef.current?.click()} disabled={busy} className={chipBtn(false, '')}>
            <ImageIcon size={13} /> Galería
          </button>
          {task.requires_photo === 1 && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600">requerida para completar</span>
          )}
        </>
      ) : null}
    </div>
  );
}

// ── Detalle en línea ─────────────────────────────────────────────────────────
function TaskDetail({ task, assignees, onClose, onPhotoChanged }: {
  task: Task; assignees: Assignee[]; onClose: (changed: boolean) => void; onPhotoChanged: () => void;
}) {
  const { prompt, dialog: promptDialog } = usePromptDialog();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [due, setDue] = useState(task.complete_by || '');
  const [who, setWho] = useState(task.assigned_user_id ? String(task.assigned_user_id) : '');
  const [high, setHigh] = useState(task.priority === 'high');
  const [saving, setSaving] = useState(false);
  const [askDelete, setAskDelete] = useState(false);   // borrar = irreversible: diálogo del manager, no confirm nativo
  const resolved = task.status !== 'open';

  const save = async () => {
    const t = title.trim();
    if (!t) { toast.error('El título es obligatorio'); return; }
    setSaving(true);
    try {
      await req('PATCH', `/${task.task_id}`, {
        title: t, description: description.trim() || null, complete_by: due || null,
        assigned_user_id: who ? Number(who) : null, priority: high ? 'high' : 'normal',
      });
      toast.success('Tarea actualizada'); onClose(true);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'No se pudo guardar'); setSaving(false); }
  };
  const setStatus = async (status: Task['status']) => {
    let skip_reason: string | undefined;
    if (status === 'skipped') {
      const reason = await prompt({
        title: '¿Por qué no se hizo?',
        message: 'El motivo queda en el historial de la tarea.',
        placeholder: 'Ej. faltó material, se cerró temprano…',
        confirmLabel: 'Omitir tarea',
        required: true,
      });
      if (reason === null) return;
      if (!reason.trim()) { toast.error('El motivo es obligatorio para omitir'); return; }
      skip_reason = reason.trim();
    }
    try { await req('PATCH', `/${task.task_id}`, { status, skip_reason }); onClose(true); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'No se pudo actualizar'); }
  };
  const remove = async () => {
    setAskDelete(false);
    try { await req('DELETE', `/${task.task_id}`); onClose(true); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'No se pudo eliminar'); }
  };

  return (
    <div className="mt-1 rounded-xl border border-line bg-surface-2/60 p-3">
      {resolved && (
        <div className={`mb-2 rounded-lg px-3 py-2 text-xs ${task.status === 'done' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
          {task.status === 'done' ? '✅ Completada' : '⚠️ Omitida'}
          {task.done_by_name ? ` por ${task.done_by_name}` : ''}{task.done_at ? ` · ${fmtDT(task.done_at)}` : ''}
          {task.status === 'skipped' && task.skip_reason ? <div className="mt-0.5 italic">"{task.skip_reason}"</div> : null}
        </div>
      )}
      <div className="space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
          placeholder="Descripción / detalle (opcional)…" className={`${inputCls} resize-y`} />
        <div className="flex flex-wrap items-center gap-2">
          <select value={who} onChange={(e) => setWho(e.target.value)} className={`${inputCls} max-w-[160px] !py-1.5 text-xs`}>
            <option value="">Sin responsable</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <label className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2">
            Vence <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="bg-transparent text-xs outline-none" />
          </label>
          <button type="button" onClick={() => setHigh((v) => !v)} aria-pressed={high}
            className={chipBtn(high, 'border-rose-400 bg-rose-500/15 text-rose-600')}>
            <Flag size={13} /> Alta
          </button>
        </div>
        {/* Evidencia: visible siempre que exista o que la tarea siga abierta */}
        {(task.photo_path || !resolved) && (
          <div className="rounded-lg border border-line bg-surface-2/40 p-2">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-2">Foto de evidencia</p>
            <PhotoEvidence task={task} onChanged={onPhotoChanged} />
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!resolved ? (
          <>
            <button onClick={() => setStatus('done')} disabled={task.requires_photo === 1 && !task.photo_path}
              title={task.requires_photo === 1 && !task.photo_path ? 'Primero toma la foto de evidencia' : 'Completar'}
              className="flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-2.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-40">
              <Check size={14} /> Completar
            </button>
            {task.template_id != null && (
              <button onClick={() => setStatus('skipped')}
                className="flex h-8 items-center gap-1 rounded-lg border border-amber-500/50 px-2.5 text-xs font-medium text-amber-600 transition hover:bg-amber-500/10">
                <CircleSlash size={14} /> Omitir
              </button>
            )}
          </>
        ) : (
          <button onClick={() => setStatus('open')} className={chipBtn(false, '')}>Reabrir</button>
        )}
        {task.template_id == null && (
          <button onClick={() => setAskDelete(true)} className="flex h-8 items-center gap-1 rounded-lg border border-line px-2.5 text-xs text-content-2 transition hover:border-rose-400 hover:text-rose-500">
            <Trash2 size={14} /> Eliminar
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => onClose(false)} className={chipBtn(false, '')}><X size={14} /> Cerrar</button>
          <button onClick={save} disabled={saving}
            className="flex h-8 items-center gap-1 rounded-lg bg-accent-solid px-3 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-40">
            <Save size={14} /> Guardar
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={askDelete}
        title="Eliminar tarea"
        icon={<Trash2 className="mr-2.5 h-5 w-5 shrink-0 text-rose-500" />}
        confirmLabel="Eliminar"
        cancelLabel="Conservar"
        message={<p>Se elimina «{task.title}». Esta acción no se puede deshacer.</p>}
        onCancel={() => setAskDelete(false)}
        onConfirm={remove}
      />
      {promptDialog}
    </div>
  );
}

// ── Historial ────────────────────────────────────────────────────────────────
function HistoryView() {
  const [tasks, setTasks] = useState<Task[] | null>(null);
  useEffect(() => {
    req<{ data: Task[] }>('GET', '?scope=all').then((r) => setTasks(r.data)).catch(() => setTasks([]));
  }, []);
  const groups = useMemo(() => {
    const resolved = (tasks || []).filter((t) => t.status !== 'open' && t.done_at)
      .sort((a, b) => (b.done_at || '').localeCompare(a.done_at || '')).slice(0, 200);
    const byDay = new Map<string, Task[]>();
    for (const t of resolved) {
      const d = new Date(t.done_at!);
      const key = isNaN(d.getTime()) ? String(t.done_at) : d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(t);
    }
    return [...byDay.entries()];
  }, [tasks]);

  if (tasks === null) return <p className="py-6 text-center text-sm text-content-2">Cargando…</p>;
  if (groups.length === 0) return <p className="py-6 text-center text-sm text-content-2">Aún no hay tareas resueltas.</p>;
  return (
    <div className="space-y-4">
      {groups.map(([day, items]) => (
        <div key={day}>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-2">{day}</p>
          <ul className="space-y-1">
            {items.map((t) => (
              <li key={t.task_id} className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-surface-2">
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-white ${t.status === 'done' ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                  {t.status === 'done' ? <Check size={13} /> : <CircleSlash size={12} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-content">
                    {t.template_id != null && <Repeat size={11} className="mr-1 inline text-content-2" />}
                    {t.title}
                  </p>
                  <p className="truncate text-[11px] text-content-2">
                    {t.status === 'done' ? 'Hecha' : 'Omitida'}
                    {t.done_by_name ? ` por ${t.done_by_name}` : ''}
                    {t.done_at ? ` · ${new Date(t.done_at).toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' })}` : ''}
                    {t.status === 'skipped' && t.skip_reason ? ` — "${t.skip_reason}"` : ''}
                  </p>
                </div>
                {t.photo_path && (
                  <a href={photoSrc(t.photo_path)} target="_blank" rel="noreferrer" title="Ver evidencia">
                    <img src={photoSrc(t.photo_path)} alt="Evidencia" className="h-9 w-9 rounded-md border border-line object-cover" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ── Recurrentes ──────────────────────────────────────────────────────────────
// ── Evidencias (F4): galería de las fotos archivadas, agrupadas por día ──────
function EvidenceView() {
  const [tasks, setTasks] = useState<Task[] | null>(null);
  useEffect(() => {
    req<{ data: Task[] }>('GET', '?scope=all').then((r) => setTasks(r.data)).catch(() => setTasks([]));
  }, []);
  const groups = useMemo(() => {
    const conFoto = (tasks || []).filter((t) => t.photo_path)
      .sort((a, b) => (b.done_at || b.created_at || '').localeCompare(a.done_at || a.created_at || ''));
    const byDay = new Map<string, { label: string; items: Task[] }>();
    for (const t of conFoto) {
      const src = t.done_at || t.created_at;
      const d = new Date(src!);
      const bad = !src || isNaN(d.getTime());
      const key = bad ? String(src)
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const label = bad ? String(src) : d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
      if (!byDay.has(key)) byDay.set(key, { label, items: [] });
      byDay.get(key)!.items.push(t);
    }
    return [...byDay.values()];
  }, [tasks]);

  if (tasks === null) return <p className="py-6 text-center text-sm text-content-2">Cargando…</p>;
  if (groups.length === 0) {
    return <p className="py-6 text-center text-sm text-content-2">
      Aún no hay evidencias archivadas. Las fotos que se tomen en las tareas aparecerán aquí.</p>;
  }
  return (
    <div className="space-y-5">
      {groups.map((g, gi) => (
        <div key={`${gi}-${g.label}`}>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-2">{g.label}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {g.items.map((t) => (
              <a key={t.task_id} href={photoSrc(t.photo_path!)} target="_blank" rel="noreferrer" title="Ver evidencia completa"
                className="group overflow-hidden rounded-xl border border-line bg-surface-2/50 transition hover:shadow-md">
                <img src={photoSrc(t.photo_path!)} alt={t.title} loading="lazy"
                  className="aspect-square w-full object-cover transition group-hover:scale-[1.02]" />
                <div className="p-1.5">
                  <p className="truncate text-xs font-medium text-content">
                    {t.status === 'skipped' ? '⚠️ ' : ''}{t.title}
                  </p>
                  <p className="truncate text-[10px] text-content-2">
                    {t.done_by_name ? `${firstName(t.done_by_name)} · ` : ''}
                    {t.done_at ? new Date(t.done_at).toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' }) : 'abierta'}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TemplatesView({ assignees }: { assignees: Assignee[] }) {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [title, setTitle] = useState('');
  const [recur, setRecur] = useState<Recur>('daily');
  const [days, setDays] = useState<number[]>([]);
  const [monthDay, setMonthDay] = useState(1);
  const [high, setHigh] = useState(false);
  const [photo, setPhoto] = useState(false);
  const [who, setWho] = useState('');
  const [saving, setSaving] = useState(false);
  const [askDelete, setAskDelete] = useState<Template | null>(null);   // borrar recurrente = irreversible
  const load = () => req<{ data: Template[] }>('GET', '/templates').then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  useEffect(() => { load(); }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || saving) return;
    if (recur === 'weekly' && days.length === 0) { toast.error('Selecciona al menos un día de la semana'); return; }
    setSaving(true);
    try {
      await req('POST', '/templates', {
        title: t, recur,
        weekdays: recur === 'weekly' ? [...days].sort().join(',') : null,
        month_day: recur === 'monthly' ? monthDay : null,
        priority: high ? 'high' : 'normal',
        requires_photo: photo,
        assigned_user_id: who ? Number(who) : null,
      });
      setTitle(''); setHigh(false); setPhoto(false); setWho(''); setDays([]);
      await load(); toast.success('Tarea recurrente creada');
    } catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo crear'); }
    finally { setSaving(false); }
  };
  const toggleActive = async (tpl: Template) => {
    try { await req('PATCH', `/templates/${tpl.template_id}`, { active: tpl.active ? 0 : 1 }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo actualizar'); }
  };
  const togglePhoto = async (tpl: Template) => {
    try { await req('PATCH', `/templates/${tpl.template_id}`, { requires_photo: !tpl.requires_photo }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo actualizar'); }
  };
  const remove = async (tpl: Template) => {
    setAskDelete(null);
    try { await req('DELETE', `/templates/${tpl.template_id}`); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo eliminar'); }
  };

  return (
    <div>
      <form onSubmit={add} className="mb-3 space-y-2 rounded-xl border border-line bg-surface-2/50 p-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nueva tarea recurrente…" className={inputCls} />
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5">
            {(['daily', 'weekly', 'monthly'] as Recur[]).map((v) => (
              <button key={v} type="button" onClick={() => setRecur(v)}
                className={`rounded-md px-2 py-1 text-xs font-medium transition ${recur === v ? 'bg-accent-solid text-white' : 'text-content-2 hover:text-content'}`}>
                {v === 'daily' ? 'Diaria' : v === 'weekly' ? 'Semanal' : 'Mensual'}
              </button>
            ))}
          </div>
          {recur === 'monthly' && (
            <label className="flex items-center gap-1.5 text-xs text-content-2">
              el día
              <NumInput min={1} max={31} value={monthDay} emptyAs={1}
                onValue={(n) => setMonthDay(Math.min(31, Math.max(1, n || 1)))}
                className={`${inputCls} w-14 !px-2 !py-1 text-xs`} />
            </label>
          )}
          <select value={who} onChange={(e) => setWho(e.target.value)} className={`${inputCls} max-w-[150px] !py-1.5 text-xs`}>
            <option value="">Sin responsable</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button type="button" onClick={() => setHigh((v) => !v)} aria-pressed={high} className={chipBtn(high, 'border-rose-400 bg-rose-500/15 text-rose-600')}>
            <Flag size={13} /> Alta
          </button>
          <button type="button" onClick={() => setPhoto((v) => !v)} aria-pressed={photo}
            title="Para completarla habrá que subir foto de evidencia"
            className={chipBtn(photo, 'border-accent/50 bg-accent/10 text-accent')}>
            <Camera size={13} /> Foto
          </button>
          <button type="submit" disabled={!title.trim() || saving}
            className="ml-auto flex h-8 items-center gap-1 rounded-lg bg-accent-solid px-2.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-40">
            <Plus size={13} /> Crear
          </button>
        </div>
        {recur === 'weekly' && (
          <div className="flex flex-wrap gap-1">
            {DIAS.map((d, i) => (
              <button key={d} type="button" onClick={() => setDays((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i])}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${days.includes(i) ? 'bg-accent-solid text-white' : 'bg-surface-2 text-content-2 hover:text-content'}`}>
                {d}
              </button>
            ))}
          </div>
        )}
      </form>
      {templates === null ? (
        <p className="py-4 text-center text-sm text-content-2">Cargando…</p>
      ) : templates.length === 0 ? (
        <p className="py-4 text-center text-sm text-content-2">
          Sin recurrentes aún. Crea la primera arriba — cada día que aplique aparecerá sola en Tareas.
        </p>
      ) : (
        <ul className="space-y-1">
          {templates.map((tpl) => (
            <li key={tpl.template_id} className={`group flex items-center gap-2.5 rounded-lg px-2 py-2 transition hover:bg-surface-2 ${tpl.active ? '' : 'opacity-50'}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-content">
                  {tpl.priority === 'high' && <Flag size={11} className="mr-1 inline text-rose-500" />}
                  {tpl.title}
                </p>
                <p className="truncate text-[11px] text-content-2">
                  {recurLabel(tpl)}{tpl.requires_photo ? ' · 📷 con foto' : ''}{tpl.assignee ? ` · ${tpl.assignee}` : ''}{tpl.active ? '' : ' · pausada'}
                </p>
              </div>
              <button onClick={() => togglePhoto(tpl)} title={tpl.requires_photo ? 'Quitar requisito de foto' : 'Exigir foto de evidencia'}
                className={`shrink-0 transition ${tpl.requires_photo ? 'text-accent' : 'text-content-2 hover:text-content'}`}>
                <Camera size={15} />
              </button>
              <button onClick={() => toggleActive(tpl)} title={tpl.active ? 'Pausar' : 'Reanudar'} className="shrink-0 text-content-2 transition hover:text-content">
                {tpl.active ? <Pause size={15} /> : <Play size={15} />}
              </button>
              <button onClick={() => setAskDelete(tpl)} title="Eliminar" className="shrink-0 text-content-2 transition hover:text-rose-500">
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={askDelete != null}
        title="Eliminar tarea recurrente"
        icon={<Trash2 className="mr-2.5 h-5 w-5 shrink-0 text-rose-500" />}
        confirmLabel="Eliminar"
        cancelLabel="Conservar"
        message={
          <>
            <p>Se elimina la recurrente «{askDelete?.title}» y deja de generar tareas nuevas.</p>
            <p className="mt-2 text-xs text-content-3">El historial ya resuelto se conserva.</p>
          </>
        }
        onCancel={() => setAskDelete(null)}
        onConfirm={() => { if (askDelete) remove(askDelete); }}
      />
    </div>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────
export default function TasksPage() {
  const { prompt, dialog: promptDialog } = usePromptDialog();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [high, setHigh] = useState(false);
  const [photo, setPhoto] = useState(false);
  const [who, setWho] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<'tareas' | 'historial' | 'recurrentes' | 'evidencias'>('tareas');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Filtro «ver las de Fulano» (F2 plan de tareas): client-side sobre la lista ya cargada.
  // '' = todas · 'none' = sin responsable · id del assignee.
  const [filtroWho, setFiltroWho] = useState('');
  // Recurrentes solo con nivel manage (owner/manager): al staff ni se le ofrece (403 = red, no UX).
  // El contexto expone `currentUser`, no `user`: con el nombre viejo llegaba `undefined` a
  // hasModule → 'none' → la pestaña «Recurrentes» no podía verse ni siendo owner.
  const { currentUser } = useAuth();
  const canManage = hasModule(currentUser, 'tasks', 'manage');

  const load = () => req<{ data: Task[]; summary: Summary }>('GET', '?scope=home')
    .then((r) => { setTasks(r.data); setSummary(r.summary); })
    .catch(() => setTasks((prev) => prev ?? []));
  useEffect(() => {
    load();
    req<{ data: Assignee[] }>('GET', '/assignees').then((r) => setAssignees(r.data)).catch(() => {});
  }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      await req('POST', '', { title: t, complete_by: due || null, priority: high ? 'high' : 'normal', requires_photo: photo, assigned_user_id: who ? Number(who) : null });
      setTitle(''); setDue(''); setHigh(false); setPhoto(false); setWho('');
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo crear'); }
    finally { setSaving(false); }
  };

  const toggle = async (t: Task) => {
    const next = t.status === 'open' ? 'done' : 'open';
    if (next === 'done' && t.requires_photo === 1 && !t.photo_path) {
      toast.error('Esta tarea requiere foto de evidencia — ábrela y toma la foto');
      setExpandedId(t.task_id);
      return;
    }
    setTasks((prev) => prev?.map((x) => x.task_id === t.task_id ? { ...x, status: next } : x) || null);
    try { await req('PATCH', `/${t.task_id}`, { status: next }); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo actualizar'); }
    load();
  };

  const skip = async (t: Task) => {
    const reason = await prompt({
      title: `Omitir "${t.title}"`,
      message: '¿Por qué no se hizo? El motivo queda en el historial.',
      placeholder: 'Ej. faltó material, se cerró temprano…',
      confirmLabel: 'Omitir tarea',
      required: true,
    });
    if (reason === null) return;
    if (!reason.trim()) { toast.error('El motivo es obligatorio para omitir'); return; }
    try { await req('PATCH', `/${t.task_id}`, { status: 'skipped', skip_reason: reason.trim() }); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo omitir'); }
    load();
  };

  const remove = async (t: Task) => {
    setTasks((prev) => prev?.filter((x) => x.task_id !== t.task_id) || null);
    try { await req('DELETE', `/${t.task_id}`); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'No se pudo eliminar'); }
    load();
  };

  const viewBtn = (v: 'historial' | 'recurrentes' | 'evidencias', icon: React.ReactNode, label: string) => (
    <button onClick={() => { if (view === v) { setView('tareas'); load(); } else setView(v); }}
      aria-pressed={view === v}
      className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${view === v ? 'border-accent/50 bg-accent/10 text-accent' : 'border-line text-content-2 hover:bg-surface-2 hover:text-content'}`}>
      {icon} {view === v ? 'Volver a tareas' : label}
    </button>
  );

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-4 sm:px-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-content">
          <ListTodo size={20} className="text-accent" /> Tareas del equipo
        </h2>
        <div className="flex items-center gap-2">
          {summary && (summary.overdue > 0 || summary.today > 0) && view === 'tareas' && (
            <div className="flex items-center gap-1.5 text-[10px] font-semibold">
              {summary.overdue > 0 && <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-rose-600">{summary.overdue} vencidas</span>}
              {summary.today > 0 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-600">{summary.today} hoy</span>}
            </div>
          )}
          {viewBtn('historial', <History size={13} />, 'Historial')}
          {viewBtn('evidencias', <ImageIcon size={13} />, 'Evidencias')}
          {canManage && viewBtn('recurrentes', <Repeat size={13} />, 'Recurrentes')}
        </div>
      </div>

      {view === 'historial' ? <HistoryView />
        : view === 'evidencias' ? <EvidenceView />
        : view === 'recurrentes' ? <TemplatesView assignees={assignees} />
        : <>
          <form onSubmit={add} className="mb-4 space-y-2 rounded-xl border border-line bg-surface-2/50 p-3">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nueva tarea…" className={inputCls} />
            <div className="flex flex-wrap items-center gap-2">
              <select value={who} onChange={(e) => setWho(e.target.value)} className={`${inputCls} max-w-[150px] !py-1.5 text-xs`}>
                <option value="">Sin responsable</option>
                {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2">
                Vence <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="bg-transparent text-xs outline-none" />
              </label>
              <button type="button" onClick={() => setHigh((v) => !v)} aria-pressed={high} className={chipBtn(high, 'border-rose-400 bg-rose-500/15 text-rose-600')}>
                <Flag size={13} /> Alta
              </button>
              <button type="button" onClick={() => setPhoto((v) => !v)} aria-pressed={photo}
                title="Para completarla habrá que subir foto de evidencia"
                className={chipBtn(photo, 'border-accent/50 bg-accent/10 text-accent')}>
                <Camera size={13} /> Foto
              </button>
              <button type="submit" disabled={!title.trim() || saving}
                className="ml-auto flex h-8 items-center gap-1 rounded-lg bg-accent-solid px-3 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-40">
                <Plus size={14} /> Agregar
              </button>
            </div>
          </form>

          {assignees.length > 0 && tasks !== null && tasks.length > 0 && (
            <div className="mb-2 flex justify-end">
              <select value={filtroWho} onChange={(e) => setFiltroWho(e.target.value)}
                title="Ver solo las tareas de una persona"
                className={`${inputCls} max-w-[160px] !py-1.5 text-xs`}>
                <option value="">Todas las personas</option>
                <option value="none">Sin responsable</option>
                {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}
          {tasks === null ? (
            <p className="py-8 text-center text-sm text-content-2">Cargando…</p>
          ) : tasks.length === 0 ? (
            <p className="py-8 text-center text-sm text-content-2">Sin tareas pendientes. 🎉</p>
          ) : (
            <ul className="space-y-1">
              {tasks.filter((t) => !filtroWho
                || (filtroWho === 'none' ? t.assigned_user_id == null : String(t.assigned_user_id) === filtroWho)
              ).map((t) => {
                const resolved = t.status !== 'open';
                const chip = resolved ? null : dueChip(t);
                const expanded = expandedId === t.task_id;
                return (
                  <li key={t.task_id} className="rounded-lg px-1 py-1.5 transition hover:bg-surface-2/60">
                    <div className="group flex items-center gap-2.5">
                      <button onClick={() => toggle(t)} title={resolved ? 'Reabrir' : 'Completar'}
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                          t.status === 'done' ? 'border-emerald-500 bg-emerald-500 text-white'
                          : t.status === 'skipped' ? 'border-amber-500 bg-amber-500 text-white'
                          : 'border-content-3 hover:border-accent'}`}>
                        {t.status === 'done' && <Check size={13} />}
                        {t.status === 'skipped' && <CircleSlash size={12} />}
                      </button>
                      <button type="button" onClick={() => setExpandedId(expanded ? null : t.task_id)}
                        className={`min-w-0 flex-1 truncate text-left text-sm ${resolved ? 'text-content-2 line-through' : 'text-content'}`}
                        title="Ver / editar">
                        {t.template_id != null && <Repeat size={11} className="mr-1 inline text-content-2" />}
                        {t.priority === 'high' && !resolved && <Flag size={11} className="mr-1 inline text-rose-500" />}
                        {t.requires_photo === 1 && !resolved && <Camera size={11} className={`mr-1 inline ${t.photo_path ? 'text-emerald-500' : 'text-amber-500'}`} />}
                        {t.title}
                      </button>
                      {t.assignee && (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold text-accent" title={t.assignee}>
                          <UserRound size={10} /> {firstName(t.assignee)}
                        </span>
                      )}
                      {/* Turno de caja (F2): aquí solo se muestra — el candado se administra en la agenda */}
                      {t.shift && !resolved && (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-semibold text-content-2" title={`Amarra el corte del turno ${t.shift}`}>
                          <Clock size={10} /> {t.shift}
                        </span>
                      )}
                      {chip && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.cls}`}>{chip.label}</span>}
                      {!resolved && t.template_id != null && (
                        <button onClick={() => skip(t)} title="Omitir con motivo"
                          className="shrink-0 text-content-2 opacity-0 transition hover:text-amber-500 group-hover:opacity-100">
                          <CircleSlash size={14} />
                        </button>
                      )}
                      {t.template_id == null && (
                        <button onClick={() => remove(t)} title="Eliminar"
                          className="shrink-0 text-content-2 opacity-0 transition hover:text-rose-500 group-hover:opacity-100">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {expanded && (
                      <TaskDetail key={`${t.task_id}-${t.photo_path || ''}-${t.status}`} task={t} assignees={assignees}
                        onClose={(changed) => { setExpandedId(null); if (changed) load(); }}
                        onPhotoChanged={load} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>}
      {promptDialog}
    </div>
  );
}
